import { createFileRoute } from "@tanstack/react-router";

import { SiteShell } from "@/components/marketing/site-shell";
import { LegalPage, LegalSection } from "@/components/marketing/legal-page";

const TITLE = "Refund & Cancellation Policy — AROS FLOW";
const DESC =
  "How to cancel an ArosFlow subscription, when a refund is given, and how the Academy bundle is treated.";

export const Route = createFileRoute("/legal/refunds")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SiteShell>
      <LegalPage title="Refund &amp; Cancellation Policy" summary={DESC}>
        <LegalSection title="Cancelling a subscription">
          <p>
            You can cancel at any time from your billing settings, or by emailing us. Cancellation
            takes effect at the end of the period you have already paid for — you keep full access
            until then, and you are not charged again. We do not lock anyone into a contract and
            there is no cancellation fee.
          </p>
        </LegalSection>

        <LegalSection title="Refunds on monthly plans">
          <ul>
            <li>
              <strong>First 7 days:</strong> if ArosFlow is not right for you, email us within 7
              days of your first payment on a plan and we will refund that payment in full.
            </li>
            <li>
              <strong>Duplicate or accidental charges:</strong> refunded in full, always.
            </li>
            <li>
              <strong>Service failure:</strong> if a feature you paid for did not work and we could
              not fix it, tell us and we will refund the affected period.
            </li>
            <li>
              <strong>Part-used months:</strong> after the first 7 days we do not usually refund a
              month already in progress, because live property lookups and AI runs cost us money as
              you use them. Ask anyway if your situation is unusual — we would rather be fair than
              rigid.
            </li>
          </ul>
        </LegalSection>

        <LegalSection title="Allowances and credits">
          <p>
            Monthly allowances reset each billing period and are not refundable or transferable when
            unused. Purchased credits are refundable only while unused.
          </p>
        </LegalSection>

        <LegalSection title="ArosFlow Academy (one-time purchase)">
          <p>
            An existing Academy bundle purchase is refundable in full within 14 days of purchase.
            Where a course is still being authored, this is stated clearly before you buy and a
            refund is always available while it remains unfinished.
          </p>
        </LegalSection>

        <LegalSection title="Early-access features">
          <p>
            Features marked early access are not billed as working features. If you ever believe you
            were charged for something advertised as live that was not, tell us and we will refund
            it.
          </p>
        </LegalSection>

        <LegalSection title="How to request a refund">
          <p>
            Email <a href="mailto:support@arosflow.com">support@arosflow.com</a> from your account
            email with the charge date. We aim to respond within 2 business days; approved refunds
            return to your original payment method, typically within 5–10 business days depending on
            your bank.
          </p>
        </LegalSection>
      </LegalPage>
    </SiteShell>
  );
}
